'use client';

import React, { useState } from 'react';

import { cn } from '@cineamo/ui/src/helpers/cn';

import { GretaIcon, GretaIconVariant } from '@cineamo/ui/src/icons/ic-greta';

import { useAnalytics } from '../providers/analyticsProvider';

import GretaInfoModal from './GretaInfoModal';

export type GretaLabelProps = {
    /** Whether the movie has accessibility features — nothing renders unless it has an accessibility feature. */
    hasMovieAccessibilityFeatures?: boolean;
    /** Title reported with the click event. */
    movieTitle?: string;
    /** Icon colourway; defaults to the GretaIcon's own (`blackYellow`). */
    variant?: GretaIconVariant;
    iconClassName?: string;
    className?: string;
};

/**
 * GRETA accessibility badge: renders the GRETA icon when the movie offers audio description,
 * closed captions or sound amplification, and opens the info modal on click.
 *
 * Owns the whole behaviour — the feature check, the open state, the click tracking and the modal —
 * so callers only hand it a movie.
 */
export default function GretaLabel(props: GretaLabelProps): React.JSX.Element | null {
    const [isOpen, setIsOpen] = useState(false);
    const { track } = useAnalytics();

    if (!props.hasMovieAccessibilityFeatures) {
        return null;
    }

    function openModal(): void {
        track('GRETA Button Clicked', { movie: props.movieTitle || 'N/A' });
        setIsOpen(true);
    }

    return (
        // `flex` keeps the badge a block-level box. As an inline-level box it would sit on its
        // wrapper's baseline, and the line box would reserve descender space below the icon —
        // enough to push the badge above the centre of the text it is aligned with.
        <span className={cn('pointer-events-auto relative z-[1] flex flex-none items-center', props.className)}>
            {/* A role="button" span rather than a <button>: the badge sits inside clickable parents
                (program rows, list items) where a nested native button stays activatable even
                when the inner handler calls stopPropagation. */}
            <span
                role="button"
                tabIndex={0}
                className="cursor-pointer hover:opacity-70 flex"
                aria-label="GRETA information"
                onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    openModal();
                }}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.stopPropagation();
                        event.preventDefault();
                        openModal();
                    }
                }}>
                <GretaIcon
                    className={cn('w-24 h-24', props.iconClassName)}
                    variant={props.variant}
                />
            </span>
            {isOpen && (
                <GretaInfoModal
                    isOpen={isOpen}
                    onClose={() => setIsOpen(false)}
                />
            )}
        </span>
    );
}
